import './styles.css';

import React, { ChangeEventHandler, useState } from 'react';
import styled from 'styled-components';
import { path } from 'ramda';
import { FormikContextType } from 'formik';
import { Checkbox, Row, Col, Radio, Space, Button, Popover } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CaretDownOutlined, InfoCircleFilled } from '@ant-design/icons';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';

import {
  SendCommunicationFirstStepForm,
  SendCommunicationCustomersSelection as CustomersSelection,
  TargetType,
} from 'types';
import { useIntl } from 'common/config/i18n';
import FormError from 'components/molecules/form-error';
import { useTheme } from 'common/styling/theme';

import InputSelect from '../select';

export type Props = {
  formik: FormikContextType<SendCommunicationFirstStepForm>;
  stationsConcerned: {
    shortCode: string;
    uicStation: string;
  }[];
  isBulkCommunication?: boolean;
  targetType: TargetType;
  ServicesPassengersConcernedByCommunicationErrorMessage?: string;
  onTouch: () => void;
  setTargetType: (id: TargetType) => void;
  setOdsSummaryContent: (content: string[][]) => void;
};

export type TargetClientTypeProps = {
  formik: Props['formik'];
  isAllStationsChecked: boolean;
  hasNoStationsInCommon: boolean;
  shouldDisplaySelectedStationsCol: boolean;
  shouldDisplayExcludedStationsCol: boolean;
  hasOdsSummaryButton: boolean;
  isBulkCommunication?: boolean;
  stations: {
    shortCode: string;
    uicStation: string;
  }[];
  stationsConcerned: {
    shortCode: string;
    uicStation: string;
  }[];
  excludedStations: {
    shortCode: string;
    uicStation: string;
  }[];
  handleCustomersSelectedChange: (e: CheckboxChangeEvent) => void;
  handleChangeCheckStations: (e: CheckboxValueType[]) => void;
  handleChangeCheckAllStations: (e: CheckboxChangeEvent) => void;
  handleChangeCheckExcludeStations: (e: CheckboxValueType[]) => void;
  setOdsSummaryContent: (content: string[][]) => void;
};

export type TargetDVNumbersProps = {
  formik: Props['formik'];
  httpErrorMessage?: string;
  handleChangeDvNumbers: ChangeEventHandler<HTMLTextAreaElement>;
};

export const Container = styled.div``;

export const SectionTitle = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'h3'])};
  font-weight: bold;
  margin-bottom: 1.25rem;
`;

export const CheckboxGroup = styled(Checkbox.Group)`
  .ant-checkbox-group-item {
    font-weight: normal;
  }
`;

export const TextStyled = styled.div<{ isTitle?: boolean }>`
  ${({ isTitle, theme }) => isTitle && `color: ${theme.colors.secondary.main}`};
  font-size: ${path(['theme', 'font', 'size', 'h3'])};
  font-weight: bold;
  ${({ isTitle, theme }) => isTitle && `border-bottom: 2px solid ${theme.colors.grey.dark}`};
  ${({ isTitle }) => isTitle && `padding: 4px`};
`;

export const InfoIconButton = styled(Button)`
  background: none;
  border: none;
  box-shadow: none;
  :hover {
    color: black !important;
  }
`;

export const SummaryContentCol = styled(Col)`
  margin-right: 35px;
`;

export const SummaryContentItem = styled.p<{ color?: string; weight?: number; size?: string }>`
  margin-bottom: 0;
  font-weight: ${({ weight, theme }) => weight || theme.font.weight.bold};
  color: ${({ color, theme }) => color || theme.colors.common.black};
  ${({ size }) => size && `font-size: ${size}`};
`;

export const SelectedCustomers = ({
  selectedCustomersType,
  onSelectCustomersType,
  hasNoStationsInCommon,
  showTitle,
}: {
  selectedCustomersType: CustomersSelection;
  onSelectCustomersType: (e: CheckboxChangeEvent) => void;
  hasNoStationsInCommon: boolean;
  showTitle: boolean;
}) => {
  const { t } = useIntl();

  return (
    <Col sm={6}>
      {showTitle && (
        <Row>
          <Col>
            <SectionTitle>{t('form', 'IMPACTED_CUSTOMERS_SELECTION_H3')}</SectionTitle>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Radio.Group onChange={onSelectCustomersType} value={selectedCustomersType}>
            <Space direction="vertical">
              <Radio value={CustomersSelection.All}> {t('form', 'ALL_CUSTOMERS')}</Radio>
              <Radio value={CustomersSelection.Ascending} disabled={hasNoStationsInCommon}>
                {t('form', 'ASCENDING_CUSTOMERS')}
              </Radio>
              <Radio value={CustomersSelection.Descending} disabled={hasNoStationsInCommon}>
                {t('form', 'DESCENDING_CUSTOMERS')}
              </Radio>
              <Radio value={CustomersSelection.AscendingDescending} disabled={hasNoStationsInCommon}>
                {t('form', 'ASCENDING_DESCENDING_CUSTOMERS')}
              </Radio>
            </Space>
          </Radio.Group>
        </Col>
      </Row>
    </Col>
  );
};

export const SelectedStations = ({
  formik,
  stations,
  impactedStations,
  handleChangeCheckStations,
  handleChangeCheckAllStations,
  isAllStationsChecked,
}: {
  formik: Props['formik'];
  stations: {
    shortCode: string;
    uicStation: string;
  }[];
  impactedStations: string[];
  handleChangeCheckStations: (e: CheckboxValueType[]) => void;
  isAllStationsChecked: boolean;
  handleChangeCheckAllStations: (e: CheckboxChangeEvent) => void;
}) => {
  const { t } = useIntl();
  return (
    <Col sm={6}>
      <Row>
        <Col>
          <SectionTitle>{t('form', 'DEPARTURE_STATIONS_IMPACTED_H3')}</SectionTitle>
        </Col>
      </Row>
      <Row>
        <Col>
          <FormError formik={formik} fieldName="impactedStations" />
          <Space direction="vertical">
            <CheckboxGroup onChange={handleChangeCheckStations} value={impactedStations}>
              <Row>
                {stations?.map((station) => (
                  <Col key={station.uicStation}>
                    <Checkbox value={station.uicStation}>{station.shortCode}</Checkbox>
                  </Col>
                ))}
              </Row>
            </CheckboxGroup>
            <Checkbox onChange={handleChangeCheckAllStations} checked={isAllStationsChecked}>
              {t('common', 'selectAll')}
            </Checkbox>
          </Space>
        </Col>
      </Row>
    </Col>
  );
};

export const ExcludedStations = ({
  stations,
  excludedStations,
  handleChangeCheckExcludeStations,
}: {
  stations: {
    shortCode: string;
    uicStation: string;
  }[];
  excludedStations: string[];
  handleChangeCheckExcludeStations: (e: CheckboxValueType[]) => void;
}) => {
  const { t } = useIntl();
  return (
    <Col sm={6}>
      <SectionTitle>{t('form', 'ASCENDING_STATIONS_EXCLUSION_H3')}</SectionTitle>
      <Space direction="vertical">
        <CheckboxGroup style={{ width: '100%' }} onChange={handleChangeCheckExcludeStations} value={excludedStations}>
          <Row>
            {stations?.map((station) => (
              <Col key={station.uicStation}>
                <Checkbox value={station.uicStation}>{station.shortCode}</Checkbox>
              </Col>
            ))}
          </Row>
        </CheckboxGroup>
      </Space>
    </Col>
  );
};

export const OdsSummaryContent = ({
  content,
  noHeader = false,
  color,
  weight,
  size,
}: {
  content: string[][] | string;
  noHeader?: boolean;
  color?: string;
  weight?: number;
  size?: string;
}) => {
  const { t } = useIntl();

  const isContentString = typeof content === 'string';

  return (
    <Col style={{ minWidth: 300 }}>
      {!noHeader && <TextStyled isTitle>{t('sendCommunication', 'targetStations', 'odsSummaryLabel')}</TextStyled>}
      <Row style={{ padding: 10 }}>
        {isContentString && (
          <SummaryContentItem color={color} weight={weight} size={size}>
            {content}
          </SummaryContentItem>
        )}
        {!isContentString &&
          content.map((contentCols, contentColsIndex) => (
            <SummaryContentCol key={contentColsIndex}>
              {contentCols.map((rowItem, rowItemIndex) => (
                <SummaryContentItem key={rowItemIndex} color={color} weight={weight} size={size}>
                  {rowItem}
                </SummaryContentItem>
              ))}
            </SummaryContentCol>
          ))}
      </Row>
    </Col>
  );
};

export const OdsSummaryButton = ({ content }: { content: string[][] }) => {
  const { t } = useIntl();

  return (
    <Col sm={6} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Popover placement="bottomRight" content={<OdsSummaryContent content={content} />} trigger="click">
        <InfoIconButton>
          <Row style={{ display: 'flex', alignItems: 'center' }}>
            <TextStyled>{t('sendCommunication', 'targetStations', 'odsSummaryLabel')}</TextStyled>
            <div style={{ fontSize: '22px' }}>
              <InfoCircleFilled style={{ marginLeft: 5 }} />
            </div>
          </Row>
        </InfoIconButton>
      </Popover>
    </Col>
  );
};

export const NoStationsInCommon = () => {
  const { t } = useIntl();
  return (
    <Col sm={12} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Row>
        <Col>
          <TextStyled>{t('sendCommunication', 'targetStations', 'noStationsInCommon')}</TextStyled>
        </Col>
      </Row>
    </Col>
  );
};

export const SelectTargetType = ({
  targetType,
  handleSelectTargetType,
}: {
  targetType: TargetType;
  handleSelectTargetType: (id: TargetType) => void;
}) => {
  const { t } = useIntl();
  const { theme } = useTheme();

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const selectData = [
    {
      id: 'selectData',
      list: [
        {
          id: TargetType.CLIENT_TYPE,
          name: t('form', 'IMPACTED_CUSTOMERS_SELECTION_H3'),
        },
        {
          id: TargetType.DV_NUMBERS,
          name: t('form', 'IMPACTED_DV_NUMBERS_H3'),
        },
      ],
    },
  ];

  return (
    <InputSelect
      className="target-type-select"
      open={dropdownVisible}
      onDropdownVisibleChange={(visible) => setDropdownVisible(visible)}
      onChange={(id) => {
        handleSelectTargetType(id as number);
      }}
      value={targetType}
      data={selectData}
      style={{ width: 330, marginBottom: 20 }}
      optionStyle={{
        fontSize: theme.font.size.h3,
        fontWeight: 'bold',
      }}
      allowClear={false}
      suffixIcon={
        <CaretDownOutlined
          onClick={() => {
            setDropdownVisible(!dropdownVisible);
          }}
          style={{ color: theme.colors.common.black, fontSize: 20 }}
        />
      }
    />
  );
};
